import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Icon,
    IconButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Paper,
    Stack,
    Typography
} from "@mui/material";
import {
    useConectorStatus,
    useConfiguracaoEntrega
} from "../../api/DpgeIntegracaoEsfingeApi";

import {AMBIENTE} from "../../shared/env";
import {IndicadoresModoAplicacao} from "./ModoAplicacao";
import IconBadge from "../icon-badge/IconBadge";

function Status({open, onClose}) {
    const conectorStatus = useConectorStatus({enabled: open});
    const entrega = useConfiguracaoEntrega({enabled: open});

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{sx: {borderRadius: 2.5}}}
        >
            <DialogTitle sx={{p: 0}}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                    sx={{px: 3, py: 2.25, bgcolor: "surface.subtle"}}
                >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <IconBadge>monitor_heart</IconBadge>
                        <Box>
                            <Typography variant="h6" component="h2">
                                Status do sistema
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Disponibilidade dos serviços do conector
                            </Typography>
                        </Box>
                    </Stack>
                    <IconButton
                        aria-label="Fechar status do sistema"
                        onClick={onClose}
                        size="small"
                    >
                        <Icon>close</Icon>
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent dividers sx={{p: 3}}>
                <IndicadoresModoAplicacao
                    ambiente={AMBIENTE}
                    consulta={entrega}
                />
                <StatusCard status={conectorStatus} />
                <Alert severity="info" icon={<Icon>info</Icon>} sx={{mt: 2}}>
                    Esta verificação confirma a disponibilidade da API do
                    conector. Ela não comprova, isoladamente, a comunicação com
                    o fornecedor ou com o e-Sfinge/TCE-MS.
                </Alert>
            </DialogContent>
            <DialogActions sx={{px: 3, py: 2}}>
                <Button
                    variant="outlined"
                    startIcon={<Icon>refresh</Icon>}
                    disabled={conectorStatus.isFetching || entrega.isFetching}
                    onClick={() => {
                        conectorStatus.refetch();
                        entrega.refetch();
                    }}
                >
                    Atualizar status
                </Button>
                <Button variant="contained" onClick={onClose} autoFocus>
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export function StatusMenuItem({onClick}) {
    return (
        <MenuItem onClick={onClick} sx={{mx: 1, px: 1.5, borderRadius: 1.25}}>
            <ListItemIcon sx={{minWidth: 46}}>
                <IconBadge size={32}>monitor_heart</IconBadge>
            </ListItemIcon>
            <ListItemText>Status do sistema</ListItemText>
        </MenuItem>
    );
}

function StatusCard({status}) {
    const {data, error, isLoading, isFetching, dataUpdatedAt} = status;
    const situacao = obterSituacao(data?.status, error, isLoading);

    return (
        <Paper variant="outlined" sx={{p: 2.5}}>
            <Stack
                direction={{xs: "column", sm: "row"}}
                alignItems={{xs: "flex-start", sm: "center"}}
                justifyContent="space-between"
                spacing={2}
            >
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Box
                        sx={{
                            width: 50,
                            height: 50,
                            flexShrink: 0,
                            display: "grid",
                            placeItems: "center",
                            borderRadius: "50%",
                            bgcolor: `iconBackground.${situacao.tom}`,
                            color: `icon.${situacao.tom}`,
                            border: "1px solid",
                            borderColor: `iconAccent.${situacao.tom}`
                        }}
                    >
                        {isLoading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            <Icon>{situacao.icone}</Icon>
                        )}
                    </Box>
                    <Box>
                        <Typography fontWeight={700}>
                            Conector e-Sfinge
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Serviço técnico: dpge-integracao-esfinge
                        </Typography>
                    </Box>
                </Stack>
                <Chip
                    icon={
                        isFetching && !isLoading ? (
                            <CircularProgress size={14} />
                        ) : undefined
                    }
                    label={
                        isFetching && !isLoading
                            ? "Atualizando"
                            : situacao.rotulo
                    }
                    color={situacao.chip}
                    variant={
                        situacao.chip === "default" ? "outlined" : "filled"
                    }
                />
            </Stack>
            <Typography variant="body2" sx={{mt: 2}}>
                {situacao.descricao}
            </Typography>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{display: "block", mt: 1}}
            >
                {dataUpdatedAt
                    ? `Última verificação: ${new Date(
                          dataUpdatedAt
                      ).toLocaleString("pt-BR")}`
                    : "Aguardando a primeira verificação."}
            </Typography>
        </Paper>
    );
}

function obterSituacao(status, error, isLoading) {
    if (isLoading) {
        return {
            rotulo: "Consultando",
            descricao: "Consultando a disponibilidade atual da API.",
            icone: "sync",
            tom: "info",
            chip: "default"
        };
    }
    if (error) {
        return {
            rotulo: "Consulta indisponível",
            descricao:
                "Não foi possível consultar o serviço. Verifique a conexão e tente novamente.",
            icone: "cloud_off",
            tom: "error",
            chip: "error"
        };
    }
    if (status === "UP") {
        return {
            rotulo: "Operacional",
            descricao:
                "A API do conector está disponível para receber requisições.",
            icone: "check_circle",
            tom: "success",
            chip: "success"
        };
    }
    return {
        rotulo: "Indisponível",
        descricao: "A API respondeu com estado diferente do esperado.",
        icone: "error",
        tom: "error",
        chip: "error"
    };
}

export default Status;
